/* Notification Center — Recipient Group picker.
 *
 * Used by BOTH the Rule Detail → Recipients tab (view + edit) and the New Rule
 * wizard → Recipients step. A notification rule's recipients are now predefined
 * Recipient GROUPS (not ad-hoc roles): the available groups load live from the DB
 * (the store's `recipients`, hydrated from AI_NotificationRecipientGroup), and the
 * rule stores the selected group IDs (the stable Oid) — so renaming a group never
 * breaks or duplicates the link: the name shown is always the group's CURRENT
 * name, looked up by id. The actual users / roles / departments /
 * channels resolve from each group's own configuration at send time — so a user
 * in two selected groups is notified once, not duplicated.
 *
 * CONTROLLED: the parent owns `selected` (group ids) and receives every change
 * via onChange. readOnly (View) shows ONLY the selected groups. `known` supplies
 * the last-known name of a linked group that has since been DELETED, so the
 * "Group no longer exists" row can still say which group it was. */
import { useMemo, useState } from 'react';
import { useNcStore } from './store';
import { RecipientsEditor } from './RecipientsEditor';
import type { NcRecipientGroup, NcRuleGroupRef } from './data';

export function RecipientGroupPicker({ selected, onChange, readOnly = false, known = [] }: {
  /** Selected group IDs. */
  selected: string[];
  onChange: (ids: string[]) => void;
  readOnly?: boolean;
  known?: NcRuleGroupRef[];
}) {
  const allGroups = useNcStore((s) => s.recipients);   // live, DB-backed — no mock data
  // Only groups with a persisted id are selectable (a group created moments ago
  // gets its id as soon as its save returns).
  const groups = useMemo(() => allGroups.filter((g): g is NcRecipientGroup & { id: string } => !!g.id), [allGroups]);
  const [search, setSearch] = useState('');
  // Expand/collapse per selected group (collapsed by default) — reveals that
  // group's Role / Department | Users | Notify Via sub-rows directly (no
  // intermediate per-Role main row).
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleExpand = (id: string) => setExpanded((cur) => ({ ...cur, [id]: !cur[id] }));
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const selectedGroups = groups.filter((g) => selectedSet.has(g.id));
  // Selected ids whose group no longer exists (deleted) — decided by ID, never by
  // name, so a RENAMED group is never mistaken for a missing one. Shown with its
  // last-known name so the user can see/remove the stale reference.
  const orphanIds = selected.filter((id) => !groups.some((g) => g.id === id));
  const orphanName = (id: string) => known.find((k) => k.id === id)?.name || 'Unknown group';

  const q = search.trim().toLowerCase();
  const available = groups.filter((g) => !q || g.name.toLowerCase().includes(q) || g.members.toLowerCase().includes(q));

  function toggle(id: string) {
    if (selectedSet.has(id)) onChange(selected.filter((x) => x !== id));   // uncheck → remove
    else onChange([...selected, id]);                                      // check → add (no duplicate: keyed by id)
  }

  const chanChips = (g: NcRecipientGroup) =>
    g.channels.map((c) => <span key={c} className="nc-chan on" style={{ marginRight: 4 }}>{c}</span>);

  return (
    <>
      {selected.length === 0 && (
        <div className="nc-form-hint" style={{ margin: '4px 0 10px' }}>No recipient groups selected{readOnly ? '.' : ' — check one or more below.'}</div>
      )}
      {selectedGroups.map((g) => {
        const open = !!expanded[g.id];
        const members = g.recipients ?? [];
        const groupUsers = g.users ?? [];
        const hasDetail = members.length > 0 || groupUsers.length > 0;
        return (
          <div key={g.id}>
            <div className="nc-recip-row">
              <div className="nc-recip-left" style={{ gap: 6 }}>
                <button className="nc-expander" title={open ? 'Collapse' : 'Expand'} aria-expanded={open}
                  disabled={!hasDetail} onClick={() => toggleExpand(g.id)}>
                  <i className={`ti ${open ? 'ti-chevron-down' : 'ti-chevron-right'}`} />
                </button>
                <div className="nc-recip-av nc-recip-badge"><i className="ti ti-users-group" /></div>
                <div><div className="nc-recip-name">{g.name}</div><div className="nc-recip-type">{g.members}</div></div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {chanChips(g)}
                {!readOnly && <button className="nc-recip-del" title="Remove group" onClick={() => toggle(g.id)}><i className="ti ti-x" /></button>}
              </div>
            </div>
            {/* Group → Role/Department sub-rows directly (no per-Role main row),
                resolved live from the group's own configuration. */}
            {/* …role rows, then its individually selected users, in one table. */}
            {open && hasDetail && (
              <div className="nc-group-sub">
                <RecipientsEditor recipients={members} users={groupUsers} onChange={() => {}} readOnly subrowsOnly />
              </div>
            )}
          </div>
        );
      })}
      {orphanIds.map((id) => (
        <div key={id} className="nc-recip-row">
          <div className="nc-recip-left">
            <div className="nc-recip-av nc-recip-badge"><i className="ti ti-users-group" /></div>
            <div><div className="nc-recip-name">{orphanName(id)}</div><div className="nc-recip-type nc-role-nodept">Group no longer exists</div></div>
          </div>
          {!readOnly && <button className="nc-recip-del" title="Remove" onClick={() => toggle(id)}><i className="ti ti-x" /></button>}
        </div>
      ))}
      {!readOnly && (
        <div className="nc-role-add">
          <div className="nc-role-add-hd">
            <span>Add recipient groups</span>
            <input className="nc-finput nc-sm" placeholder="Search group…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 240 }} />
          </div>
          <div className="nc-role-tblwrap">
            <table className="nc-tgt-table nc-role-tbl">
              <thead><tr>
                <th style={{ width: 40, textAlign: 'center' }}></th><th>Group Name</th><th>Members</th><th style={{ width: 160 }}>Channels</th>
              </tr></thead>
              <tbody>
                {groups.length === 0 && <tr><td colSpan={4} className="nc-role-empty">No recipient groups found. Create them in Recipient Groups.</td></tr>}
                {groups.length > 0 && available.length === 0 && <tr><td colSpan={4} className="nc-role-empty">No groups match “{search}”.</td></tr>}
                {available.map((g) => {
                  const checked = selectedSet.has(g.id);
                  return (
                    <tr key={g.id} onClick={() => toggle(g.id)} style={{ cursor: 'pointer' }}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={checked} onClick={(e) => e.stopPropagation()} onChange={() => toggle(g.id)} />
                      </td>
                      <td>{g.name}</td>
                      <td>{g.members}</td>
                      <td>{chanChips(g)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="nc-role-add-ft">
            <span className="nc-role-add-hint">{selected.length ? `${selected.length} group${selected.length === 1 ? '' : 's'} selected` : 'Check one or more groups to add'}</span>
          </div>
        </div>
      )}
    </>
  );
}
